import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { API, axios } from '../../utils/api';
import { testoMancano } from '../../utils/scheda';

// /admin/agenzie → «Da sistemare»: due elenchi che prima non c'erano da nessuna parte.
//
// 1. AGENZIE DA COMPLETARE A MANO. Sono le sedici a cui l'estrattore ha provato tutte le fonti senza trovarle
//    (backend/completamento_agenzie.py): rimetterle in coda vuol dire spendere pagine per rifallire. Quello che
//    manca — comune, telefono, la pagina sul portale — lo si scrive qui, in una riga, senza aprire la scheda.
//
// 2. NON LAVORABILI SENZA MOTIVO. Ventisette agenzie sono spente e nessuna ha una riga di spiegazione: erano nate
//    da un giudizio dato su **una** casa («Com'è andata con loro per questa casa» → «Male») che spegneva l'agenzia
//    intera senza lasciare traccia. Quella strada è chiusa, ma queste restano da rivedere: per ognuna si vede
//    quante case ci sono attaccate e di quali clienti attivi, e si decide — «Confermo non lavorabile», col motivo
//    obbligatorio, oppure «Sblocca».

const CAMPI = [
  ['comune', 'Comune', 'es. Lissone'],
  ['telefono', 'Telefono', 'es. 039 1234567'],
  ['sito_web', 'Sito', 'https://…'],
];

// Una riga del primo elenco: cosa manca, e i campi per scriverlo
export const RigaDaCompletare = ({ a, onFatto }) => {
  const [valori, setValori] = useState({});
  const [attesa, setAttesa] = useState(false);
  const scrivibili = CAMPI.filter(([k]) => !a[k]);
  const salva = async () => {
    const corpo = Object.fromEntries(Object.entries(valori).map(([k, v]) => [k, (v || '').trim()]).filter(([, v]) => v));
    if (!Object.keys(corpo).length) return;
    setAttesa(true);
    try {
      await axios.put(`${API}/agenzie/${a.id}`, corpo);
      toast.success(`${a.nome}: salvato`);
      setValori({});
      onFatto();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Non salvato. Riprovate.');
    } finally { setAttesa(false); }
  };
  return (
    <div className="tr ag-sist" data-testid={`da-completare-${a.id}`}>
      <div style={{ minWidth: 0 }}>
        <b><Link to={`/admin/agenzie/${a.id}`}>{a.nome}</Link></b>
        <small>{[a.comune, testoMancano(a.mancano)].filter(Boolean).join(' · ')}</small>
      </div>
      <div className="ag-sist-campi">
        {scrivibili.map(([k, nome, esempio]) => (
          <label key={k}>
            <span className="muto">{nome}</span>
            <input value={valori[k] || ''} placeholder={esempio} aria-label={`${nome} di ${a.nome}`}
              onChange={(e) => setValori((v) => ({ ...v, [k]: e.target.value }))} />
          </label>
        ))}
        {!scrivibili.length && <span className="muto">manca solo il logo o la pagina sul portale: si aggiunge dalla scheda</span>}
      </div>
      <div className="dx">
        <button type="button" className="btn sm p" disabled={attesa || !Object.values(valori).some((v) => (v || '').trim())}
          onClick={salva}>Salva</button>
      </div>
    </div>
  );
};

// Una riga del secondo elenco: quante case, di chi, e le due decisioni
export const RigaNonLavorabile = ({ a, onFatto }) => {
  const [motivo, setMotivo] = useState('');
  const [attesa, setAttesa] = useState(false);
  const chiama = async (azione, corpo) => {
    setAttesa(true);
    try {
      await axios.post(`${API}/agenzie/${a.id}/${azione}`, corpo);
      toast.success(azione === 'sblocca' ? `${a.nome}: sbloccata` : `${a.nome}: confermata non lavorabile`);
      onFatto();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Non salvato. Riprovate.');
    } finally { setAttesa(false); }
  };
  return (
    <div className="tr ag-sist" data-testid={`non-lavorabile-${a.id}`}>
      <div style={{ minWidth: 0 }}>
        <b><Link to={`/admin/agenzie/${a.id}`}>{a.nome}</Link></b>
        <small>{[a.comune, a.telefono].filter(Boolean).join(' · ') || 'senza comune né telefono'}</small>
      </div>
      <div style={{ minWidth: 0 }}>
        {/* il numero che conta: se ci sono case di clienti attivi, lasciarla spenta costa a qualcuno */}
        <span className={a.case_attive ? 'bad' : 'muto'}>
          {a.case_attive === 0 ? 'nessuna casa attiva' : `${a.case_attive} ${a.case_attive === 1 ? 'casa attiva' : 'case attive'}`}
          {a.case > a.case_attive ? ` (${a.case} in tutto)` : ''}
        </span>
        {a.clienti_attivi?.length > 0 && <small>di {a.clienti_attivi.join(', ')}</small>}
      </div>
      <div className="ag-sist-campi">
        <label>
          <span className="muto">Motivo</span>
          <input value={motivo} placeholder="perché non è lavorabile" aria-label={`Motivo per ${a.nome}`}
            onChange={(e) => setMotivo(e.target.value)} />
        </label>
      </div>
      <div className="dx" style={{ display: 'flex', gap: '.35rem', justifyContent: 'flex-end' }}>
        <button type="button" className="btn sm" disabled={attesa || motivo.trim().length < 3}
          onClick={() => chiama('non-lavorabile', { motivo: motivo.trim() })}>Confermo non lavorabile</button>
        <button type="button" className="btn sm p" disabled={attesa}
          onClick={() => chiama('sblocca', { motivo: motivo.trim() || undefined })}>Sblocca</button>
      </div>
    </div>
  );
};

const AgenzieDaSistemare = ({ agenzie = [], onFatto }) => {
  const [quale, setQuale] = useState('completare');
  const [spente, setSpente] = useState(null);
  const carica = useCallback(() => {
    axios.get(`${API}/admin/agenzie-non-lavorabili`).then((r) => setSpente(r.data)).catch(() => setSpente({ quante: 0, agenzie: [] }));
  }, []);
  useEffect(() => { if (quale === 'spente' && spente === null) carica(); }, [quale, spente, carica]);
  const rifai = () => { setSpente(null); if (onFatto) onFatto(); };

  // «da completare a mano»: l'estrattore ha già provato tutte le sue fonti e non l'ha trovata
  const daCompletare = agenzie.filter((a) => (a.mancano || []).length && (a.completamento?.stato === 'non_trovata'));
  return (
    <section className="card r" data-testid="agenzie-da-sistemare">
      <div className="tools r" style={{ marginBottom: '.6rem' }}>
        <button type="button" className={`chip${quale === 'completare' ? ' on' : ''}`} onClick={() => setQuale('completare')}
          data-testid="chip-da-completare">Da completare a mano · {daCompletare.length}</button>
        <button type="button" className={`chip${quale === 'spente' ? ' on' : ''}`} onClick={() => setQuale('spente')}
          data-testid="chip-non-lavorabili">Non lavorabili senza motivo{spente ? ` · ${spente.quante}` : ''}</button>
      </div>

      {quale === 'completare' && (
        <div className="tbl">
          <p className="imp-nota" style={{ marginBottom: '.6rem' }}>
            L&apos;estrattore ha provato tutte le sue fonti e non le ha trovate: rimetterle in coda spenderebbe
            pagine per rifallire. Quello che manca si scrive qui.
          </p>
          {!daCompletare.length && <div className="tr tutti"><span className="muto">Nessuna: sono tutte complete, o l&apos;estrattore ci sta ancora provando.</span></div>}
          {daCompletare.map((a) => <RigaDaCompletare key={a.id} a={a} onFatto={onFatto} />)}
        </div>
      )}

      {quale === 'spente' && (
        <div className="tbl">
          <p className="imp-nota" style={{ marginBottom: '.6rem' }}>
            Spente senza che nessuno abbia scritto perché: nascevano da un giudizio dato su <b>una</b> casa, che
            spegneva l&apos;agenzia intera. Quella strada è chiusa; queste restano da rivedere.
          </p>
          {spente === null && <div className="tr tutti"><span className="muto">Carico…</span></div>}
          {spente && !spente.agenzie.length && <div className="tr tutti"><span className="muto">Nessuna: hanno tutte il loro motivo scritto.</span></div>}
          {(spente?.agenzie || []).map((a) => <RigaNonLavorabile key={a.id} a={a} onFatto={rifai} />)}
        </div>
      )}
    </section>
  );
};

export default AgenzieDaSistemare;
